import { analyticsApiClient } from "@api/analytics-api-client";
import { formatDateOnly } from "@components/analytics/in-house/utils/analytics-utils";
import { useCompanyId } from "@context-providers/company-id-provider";
import { useCallback, useEffect, useState } from "react";
/** Default number of items per page. */
const DEFAULT_ITEMS_PER_PAGE = 10;

/** Fetches project metrics breakdown data with pagination and server-side search/sort. */
export function useAnalyticsProjectsBreakdownData({
  selectedRange,
  debouncedSearchText,
  selectedMetricKey
}) {
  const companyId = useCompanyId();
  const [projectsData, setProjectsData] = useState(null);
  const [isFetchingProjects, setIsFetchingProjects] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  useEffect(() => {
    const [from, to] = selectedRange;
    if (!from || !to) {
      setProjectsData(null);
      return;
    }
    async function fetchProjectsData(fromDate, toDate) {
      setIsFetchingProjects(true);
      try {
        const query = {
          from: formatDateOnly(fromDate),
          to: formatDateOnly(toDate),
          search: debouncedSearchText.trim() || undefined,
          sortBy: selectedMetricKey,
          perPage: DEFAULT_ITEMS_PER_PAGE,
          page: currentPage
        };
        const response = await analyticsApiClient.analytics.getProjectMetrics({
          workspaceId: companyId,
          shouldUseMock: true,
          query
        });
        setProjectsData(response);
      } finally {
        setIsFetchingProjects(false);
      }
    }
    void fetchProjectsData(from, to);
  }, [companyId, selectedRange, debouncedSearchText, currentPage, selectedMetricKey]);
  const handleSetCurrentPage = useCallback(page => {
    setCurrentPage(page);
  }, []);
  return {
    projects: projectsData?.items ?? [],
    totalCount: projectsData?.total ?? 0,
    isFetchingProjects,
    currentPage,
    setCurrentPage: handleSetCurrentPage
  };
}